import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, Clock, ArrowRight, Instagram } from "lucide-react";
import { BRAND, NAV, MODELS } from "@/data/siteData";

export function BrandMark({ subtitle = false, size = "default" }) {
    const imgSize = size === "lg" ? "h-11 w-11" : "h-10 w-10";
    return (
        <div className="flex items-center gap-3" data-testid="brand-logo">
            <img
                src="/favicon.webp"
                alt="alu-ograde.ba"
                className={`${imgSize} shrink-0 object-contain`}
                width="44"
                height="44"
            />
            <div className="flex flex-col leading-none">
                <span className="font-[Cabinet_Grotesk] text-[18px] font-extrabold tracking-tight uppercase">
                    alu-ograde<span className="text-[hsl(var(--accent))]">.ba</span>
                </span>
                {subtitle && (
                    <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[hsl(var(--muted-ink))]">
                        Aluminijske ograde po mjeri
                    </span>
                )}
            </div>
        </div>
    );
}

// Convert anchor links like "/#modeli" to actual nav: when on home, scroll; when elsewhere, navigate
function NavLink({ item, onClick, mobile = false }) {
    const location = useLocation();
    const isAnchor = item.href.includes("#");
    const isInternal = item.href.startsWith("/") && !isAnchor;
    const isOnHome = location.pathname === "/";

    if (isAnchor && isOnHome) {
        const hash = item.href.split("#")[1];
        return (
            <a
                href={`#${hash}`}
                onClick={onClick}
                className={
                    mobile
                        ? "px-3 py-3 text-[15px] font-semibold uppercase tracking-wider hover:bg-[hsl(var(--paper-2))]"
                        : "text-[0.88rem] font-semibold text-[hsl(var(--ink))] transition-colors hover:text-[hsl(var(--accent))]"
                }
                data-testid={`nav-${hash}`}
            >
                {item.label}
            </a>
        );
    }
    return (
        <Link
            to={item.href}
            onClick={onClick}
            className={
                mobile
                    ? "px-3 py-3 text-[15px] font-semibold uppercase tracking-wider hover:bg-[hsl(var(--paper-2))]"
                    : "text-[0.88rem] font-semibold text-[hsl(var(--ink))] transition-colors hover:text-[hsl(var(--accent))]"
            }
            data-testid={`nav-${(isInternal ? item.href.slice(1) : item.href.replace(/[/#]/g, "")) || "home"}`}
        >
            {item.label}
        </Link>
    );
}

export function Topbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 6);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`topbar fixed inset-x-0 top-0 z-50 transition-shadow ${scrolled ? "shadow-[0_1px_0_hsl(var(--line))]" : ""}`}
        >
            <div className="container-p flex h-[78px] items-center justify-between">
                <Link to="/">
                    <BrandMark subtitle />
                </Link>

                <nav className="hidden items-center gap-7 lg:flex" aria-label="Glavna navigacija">
                    {NAV.map((n) => (
                        <NavLink key={n.href} item={n} />
                    ))}
                </nav>

                <div className="hidden items-center gap-5 lg:flex">
                    <a
                        href={BRAND.phoneHref}
                        className="flex items-center gap-2 text-sm font-semibold text-[hsl(var(--ink))] hover:text-[hsl(var(--accent))]"
                        data-testid="header-phone"
                    >
                        <Phone className="h-4 w-4" /> {BRAND.phone}
                    </a>
                    <Link to="/#kontakt" className="btn btn-primary" data-testid="header-cta">
                        Zatraži ponudu
                    </Link>
                </div>

                <button
                    className="flex h-10 w-10 items-center justify-center border-2 border-[hsl(var(--ink))] lg:hidden"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Otvori meni"
                    data-testid="menu-toggle"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {open && (
                <div className="border-t border-[hsl(var(--line))] bg-white lg:hidden" data-testid="mobile-menu">
                    <div className="container-p flex flex-col gap-1 py-4">
                        {NAV.map((n) => (
                            <NavLink key={n.href} item={n} mobile onClick={() => setOpen(false)} />
                        ))}
                        <div className="mt-3 flex flex-col gap-3 border-t border-[hsl(var(--line))] pt-4">
                            <a href={BRAND.phoneHref} className="flex items-center gap-2 px-3 text-sm font-semibold">
                                <Phone className="h-4 w-4" /> {BRAND.phone}
                            </a>
                            <Link to="/#kontakt" onClick={() => setOpen(false)} className="btn btn-primary">
                                Zatraži ponudu
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}

export function MobileStickyBar() {
    return (
        <div
            className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 border-t-2 border-[hsl(var(--ink))] bg-white lg:hidden"
            data-testid="mobile-sticky-bar"
        >
            <a
                href={BRAND.phoneHref}
                className="flex items-center justify-center gap-2 bg-[hsl(var(--ink))] px-3 py-4 text-[0.78rem] font-bold uppercase tracking-[0.12em] text-white transition-colors active:bg-black"
                data-testid="mobile-sticky-call"
            >
                <Phone className="h-4 w-4" />
                Pozovi {BRAND.phone}
            </a>
            <Link
                to="/#kontakt"
                className="flex items-center justify-center gap-2 bg-[hsl(var(--accent))] px-3 py-4 text-[0.78rem] font-bold uppercase tracking-[0.12em] text-white transition-colors active:brightness-90"
                data-testid="mobile-sticky-quote"
            >
                Brza ponuda <ArrowRight className="h-4 w-4" />
            </Link>
        </div>
    );
}

export function Footer() {
    const modelLinks = MODELS.map((m) => ({ label: m.name, to: `/modeli/${m.id}` }));
    const companyLinks = [
        { label: "Početna", to: "/" },
        { label: "Modeli", to: "/#modeli" },
        { label: "Zašto aluminij", to: "/#zasto" },
        { label: "RAL boje", to: "/#boje" },
        { label: "Reference", to: "/#reference" },
        { label: "Katalog (PDF)", to: "/katalog" },
        { label: "FAQ", to: "/#faq" },
        { label: "Kontakt", to: "/#kontakt" },
    ];

    return (
        <footer className="bg-[hsl(var(--ink))] text-[rgba(255,255,255,0.7)]" data-testid="site-footer">
            {/* Top CTA strip */}
            <div className="border-b border-white/10">
                <div className="container-p flex flex-col items-start justify-between gap-6 py-10 lg:flex-row lg:items-center">
                    <div>
                        <span className="block text-[0.7rem] font-bold uppercase tracking-[0.22em] text-[hsl(var(--accent))]">
                            Spremni za vašu novu ogradu?
                        </span>
                        <h3 className="mt-3 font-[Cabinet_Grotesk] text-2xl font-extrabold uppercase tracking-tight text-white lg:text-3xl">
                            Zatražite besplatnu ponudu.
                        </h3>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <a href={BRAND.phoneHref} className="btn btn-accent">
                            <Phone className="h-4 w-4" /> {BRAND.phone}
                        </a>
                        <Link to="/#kontakt" className="btn btn-ghost" style={{ borderColor: "rgba(255,255,255,0.2)", color: "#fff" }}>
                            Pošalji upit
                        </Link>
                    </div>
                </div>
            </div>

            <div className="container-p py-16">
                <div className="grid gap-12 lg:grid-cols-12">
                    {/* Brand column */}
                    <div className="lg:col-span-4">
                        <div className="flex items-center gap-3">
                            <img src="/favicon.webp" alt="alu-ograde.ba" className="h-11 w-11 object-contain" width="44" height="44" style={{ filter: "brightness(0) invert(1)" }} />
                            <span className="font-[Cabinet_Grotesk] text-xl font-extrabold tracking-tight uppercase text-white">
                                alu-ograde<span className="text-[hsl(var(--accent))]">.ba</span>
                            </span>
                        </div>
                        <p className="mt-6 max-w-md text-[0.95rem] leading-[1.7]">
                            Kao proizvođač kvalitetnih <strong className="text-white">aluminijskih ograda</strong>, pratimo vas od planiranja do isporuke — širom Bosne i Hercegovine. Izrada po mjeri, 10 godina garancije, 217 RAL boja.
                        </p>

                        <ul className="mt-7 space-y-2 text-[0.9rem]">
                            <li className="flex items-start gap-3">
                                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--accent))]" />
                                <span>{BRAND.address}</span>
                            </li>
                            <li>
                                <a href={BRAND.phoneHref} className="flex items-center gap-3 hover:text-[hsl(var(--accent))]" data-testid="footer-phone">
                                    <Phone className="h-4 w-4 text-[hsl(var(--accent))]" /> {BRAND.phone}
                                </a>
                            </li>
                            <li>
                                <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 hover:text-[hsl(var(--accent))]" data-testid="footer-email">
                                    <Mail className="h-4 w-4 text-[hsl(var(--accent))]" /> {BRAND.email}
                                </a>
                            </li>
                            <li>
                                <a href="https://www.instagram.com/smart.intercom" target="_blank" rel="noopener noreferrer"
                                    className="flex items-center gap-3 hover:text-[hsl(var(--accent))]" data-testid="footer-instagram">
                                    <Instagram className="h-4 w-4 text-[hsl(var(--accent))]" /> @smart.intercom
                                </a>
                            </li>
                            <li className="flex items-center gap-3">
                                <Clock className="h-4 w-4 text-[hsl(var(--accent))]" /> {BRAND.hours}
                            </li>
                        </ul>
                    </div>

                    {/* Modeli column */}
                    <div className="lg:col-span-3">
                        <div className="text-[0.7rem] uppercase tracking-[0.22em] font-bold text-white">Modeli</div>
                        <ul className="mt-5 space-y-2.5 text-[0.92rem]">
                            {modelLinks.map((m) => (
                                <li key={m.to}>
                                    <Link to={m.to} className="hover:text-[hsl(var(--accent))]">
                                        {m.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Kompanija column */}
                    <div className="lg:col-span-3">
                        <div className="text-[0.7rem] uppercase tracking-[0.22em] font-bold text-white">Kompanija</div>
                        <ul className="mt-5 space-y-2.5 text-[0.92rem]">
                            {companyLinks.map((c) => (
                                <li key={c.to}>
                                    <Link to={c.to} className="hover:text-[hsl(var(--accent))]">
                                        {c.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Informacije */}
                    <div className="lg:col-span-2">
                        <div className="text-[0.7rem] uppercase tracking-[0.22em] font-bold text-white">Informacije</div>
                        <ul className="mt-5 space-y-2.5 text-[0.92rem]">
                            <li>10 godina garancije</li>
                            <li>217 RAL boja (K7)</li>
                            <li>Izrada po mjeri</li>
                            <li>Dostava širom BiH</li>
                        </ul>
                    </div>
                </div>

                <div className="mt-14 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-6 text-xs text-[rgba(255,255,255,0.5)] sm:flex-row sm:items-center">
                    <span>© {new Date().getFullYear()} alu-ograde.ba — sva prava zadržana.</span>
                    <span>Proizvedeno u Bosni i Hercegovini · made by smart-mak</span>
                </div>
            </div>
        </footer>
    );
}

// Reveal-on-scroll hook
export function useReveal() {
    useEffect(() => {
        const els = document.querySelectorAll(".reveal");
        if (!("IntersectionObserver" in window)) {
            els.forEach((el) => el.classList.add("in"));
            return;
        }
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("in");
                        io.unobserve(e.target);
                    }
                });
            },
            { threshold: 0.12 }
        );
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, []);
}
